import React, { useMemo } from "react";
import Big from "big.js";

import { styled } from "@puzzle/theme";
import { formatMoney, capitalizeName } from "@puzzle/utils";

import { PayrollJournalMapItem, PayrollJournalMapItemCategory } from "graphql/types";
import { StyledPayrollTable, TR, TH, TD } from "./styles";
import { PayrollItemFragment } from "./graphql.generated";

const SuperScript = styled("sup", {
  marginLeft: "$0h",
  fontSize: 10,
});

export function shouldHighlight(item: Partial<PayrollJournalMapItem>) {
  return (
    // implies it's a total
    (item.itemName && ["Salary", "Commision", "Bonus"].includes(item.itemName)) ||
    item.category === PayrollJournalMapItemCategory.Employer ||
    item.category === null
  );
}

const Row = ({
  curr,
  i,
  prev,
  next,
}: {
  prev?: PayrollJournalMapItem;
  next?: PayrollJournalMapItem;
  curr: PayrollJournalMapItem;
  i: number;
}) => {
  const showCategory = i === 0 || (prev && curr.category !== prev.category);
  const isLastOfCategory = !!next && curr.category !== next.category;
  const isLastOfGroup = !!next && curr.groupId !== next.groupId;
  const isFirstTotal = !!prev && !!prev.category && curr.category === null;
  const highlighted = shouldHighlight(curr);

  return (
    <TR isLastOfGroup={isLastOfGroup} bold={isFirstTotal}>
      <TD
        id={isLastOfCategory.toString()}
        isLastOfCategory={isLastOfCategory}
        css={{
          borderBottom: isLastOfCategory
            ? "1px solid $outline-neutral-highlight"
            : "1px solid transparent !important",
        }}
      >
        {showCategory && curr.category}
      </TD>
      <TD
        highlighted={highlighted}
        bright={curr.category === null}
        hasNext={!!next && !!next.itemName}
      >
        {curr.itemName && capitalizeName(curr.itemName)}
      </TD>
      <TD
        align="right"
        highlighted={highlighted}
        bright={curr.category === null}
        hasNext={!!next && next.amount !== null}
      >
        {curr.amount && formatMoney({ currency: "USD", amount: Big(curr.amount).abs() })}
      </TD>
      <TD
        css={{ borderRight: "1px solid $outline-neutral-highlight" }}
        hasNext={!!next && !!next.journalName}
      >
        {curr.journalName}
        {curr.hasMemoLine && <SuperScript>(1)</SuperScript>}
      </TD>
      <TD
        align="right"
        hasNext={!!next && (next.journalDebit !== null || next.journalCredit !== null)}
      >
        {curr.journalDebit &&
          formatMoney({ currency: "USD", amount: Big(curr.journalDebit).abs() })}
      </TD>
      <TD
        align="right"
        hasNext={!!next && (next.journalDebit !== null || next.journalCredit !== null)}
      >
        {curr.journalCredit &&
          formatMoney({ currency: "USD", amount: Big(curr.journalCredit).abs() })}
      </TD>
    </TR>
  );
};

export const TransparencyTable = ({ payroll }: { payroll: PayrollItemFragment }) => {
  const items = payroll.journalMapping.items;
  const memoLines = payroll.journalMapping.memoLines;
  const allItems = useMemo(() => [...items, ...memoLines], [items, memoLines]);

  return (
    <div>
      <StyledPayrollTable>
        <thead>
          <TR>
            <TH colSpan={1}></TH>
            <TH colSpan={2}>From payroll provider</TH>
            <TH colSpan={3}>Posted to general ledger</TH>
          </TR>
          <TR variant="subheader">
            <TH></TH>
            <TH>Item</TH>
            <TH>Amount</TH>
            <TH css={{ borderRight: "1px solid $outline-neutral-highlight !important" }}>
              Account
            </TH>
            <TH>Debit</TH>
            <TH>Credit</TH>
          </TR>
        </thead>
        <tbody>
          {allItems.map((item, i) => {
            return (
              <Row
                i={i}
                curr={item}
                prev={allItems[i - 1]}
                next={allItems[i + 1]}
                key={`${item.itemName}--${item.category}--${item.groupId}--${item.journalName}--${i}`}
              />
            );
          })}
        </tbody>
      </StyledPayrollTable>
    </div>
  );
};
